import { type ICollectionDetails } from "./useCollections"
import { useForecastsForVariantsByDates } from "./useForecasts"
import { convertToStartOfMonthUTC, convertToEndOfMonthUTC } from "~/utils/datetime"
import { addMonths } from "date-fns"
import { utc } from "@date-fns/utc"
import { useMemo } from "react"

/**
 * Breaks a date range into month-long period objects.
 * Each object includes the month key (e.g., "2024-03"), the start, and the end date of the month.
 * Aggregates values for each month across the provided variant IDs.
 */
function mapForecastToDateValueArray(
  forecastMap: Map<string, { [key: string]: number }>,
  variantIds: string[] | undefined,
  monthlyRanges: { key: string; startDate: Date; endDate: Date }[],
): { date: Date; value: number }[] {
  return monthlyRanges.map((range) => {
    const value = (variantIds ?? []).reduce((sum, variantId) => {
      const obj = forecastMap.get(variantId)
      return sum + (obj?.[range.key] ?? 0)
    }, 0)
    return { date: range.startDate, value }
  })
}

/**
 * Returns a list of month period ranges (with key, start and end date) between startDate and endDate.
 * Each range corresponds to an entire month, keying as MM ("01"-"12"), useful for aggregating monthly data.
 */
function getMonthlyRanges(startDate: Date, endDate: Date) {
  const ranges: { key: string; startDate: Date; endDate: Date }[] = []
  let current = convertToStartOfMonthUTC(startDate)

  while (current <= endDate) {
    const monthStart = convertToStartOfMonthUTC(current)
    const monthEnd = convertToEndOfMonthUTC(current)

    ranges.push({
      key: String(monthStart.getUTCMonth() + 1).padStart(2, "0"),
      startDate: monthStart,
      endDate: monthEnd < endDate ? monthEnd : endDate,
    })

    current = addMonths(current, 1, { in: utc })
  }

  return ranges
}

export type CollectionGroupForecastData = {
  groupName: string
  collectionIds: number[]
  forecastData: { date: Date; value: number }[]
  startDate: Date
  endDate: Date
}

/**
 * Groups collections by their collection group name and returns forecast data for each group.
 * Each group aggregates variant IDs from all collections in the group.
 * @param collections - The main collections to fetch forecasts for.
 * @param comparisonCollections - Array of comparison collections to group and fetch forecasts for.
 * @param startDate - The start date of the date range for main collections.
 * @param endDate - The end date of the date range for main collections.
 * @param warehouseId - The warehouse ID to fetch forecasts for.
 * @param channel - The channel to fetch forecasts for.
 * @returns The forecast data for main collections and grouped forecast data for comparison collection groups.
 */
export function useMultiCollectionSalesForecast(
  collections: ICollectionDetails[],
  comparisonCollections: ICollectionDetails[],
  startDate: Date,
  endDate: Date,
  warehouseId?: string,
  channel?: string,
): {
  forecastData: { date: Date; value: number }[]
  comparisonGroupForecastsData: CollectionGroupForecastData[]
} {
  // Main collection variant IDs
  const allVariantsIdsOnCollections =
    collections.length > 0 && collections[0]?.variantIds
      ? collections[0].variantIds.map((id) => id.toString())
      : []

  const monthlyRangesCollections = getMonthlyRanges(startDate, endDate)

  // Group comparison collections by their group name
  const comparisonGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        groupName: string
        collections: ICollectionDetails[]
        variantIds: Set<string>
        startDate: Date
        endDate: Date
      }
    >()

    comparisonCollections.forEach((collection) => {
      const groupName = collection.collectionGroup?.name ?? collection.name ?? "Unknown"
      const existing = groups.get(groupName)

      if (existing) {
        existing.collections.push(collection)
        collection.variantIds?.forEach((id) => existing.variantIds.add(id.toString()))
        if (collection.startDate && collection.startDate < existing.startDate) {
          existing.startDate = collection.startDate
        }
        if (collection.endDate && collection.endDate > existing.endDate) {
          existing.endDate = collection.endDate
        }
      } else {
        const variantIds = new Set<string>()
        collection.variantIds?.forEach((id) => variantIds.add(id.toString()))
        groups.set(groupName, {
          groupName,
          collections: [collection],
          variantIds,
          startDate: collection.startDate ?? startDate,
          endDate: collection.endDate ?? endDate,
        })
      }
    })

    return Array.from(groups.values())
  }, [comparisonCollections, startDate, endDate])

  // Combine all variant IDs from all comparison collections for a single query
  const allComparisonVariantIds = useMemo(() => {
    const ids = new Set<string>()
    comparisonCollections.forEach((c) => {
      c.variantIds?.forEach((id) => ids.add(id.toString()))
    })
    return Array.from(ids)
  }, [comparisonCollections])

  // Get the widest date range across all comparison collections
  const comparisonDateRange = useMemo(() => {
    if (comparisonCollections.length === 0) {
      return { startDate, endDate }
    }
    const minStart = comparisonCollections.reduce(
      (min, c) => (c.startDate && c.startDate < min ? c.startDate : min),
      comparisonCollections[0]?.startDate ?? startDate,
    )
    const maxEnd = comparisonCollections.reduce(
      (max, c) => (c.endDate && c.endDate > max ? c.endDate : max),
      comparisonCollections[0]?.endDate ?? endDate,
    )
    return { startDate: minStart, endDate: maxEnd }
  }, [comparisonCollections, startDate, endDate])

  const monthlyRangesComparison = getMonthlyRanges(
    comparisonDateRange.startDate,
    comparisonDateRange.endDate,
  )

  // Fetch forecasts for main collections
  const forecastSales = useForecastsForVariantsByDates(
    monthlyRangesCollections,
    allVariantsIdsOnCollections,
    warehouseId,
    channel,
  )

  // Fetch forecasts for all comparison variants in one query
  const comparisonForecastSales = useForecastsForVariantsByDates(
    monthlyRangesComparison,
    allComparisonVariantIds,
    warehouseId,
    channel,
  )

  // Map forecast data for main collections
  const forecastData = mapForecastToDateValueArray(
    forecastSales,
    allVariantsIdsOnCollections,
    monthlyRangesCollections,
  )

  // Map forecast data for each comparison group (aggregating all collections in the group)
  const comparisonGroupForecastsData = useMemo(() => {
    return comparisonGroups.map((group) => {
      const variantIds = Array.from(group.variantIds)
      const groupMonthlyRanges = getMonthlyRanges(group.startDate, group.endDate)
      return {
        groupName: group.groupName,
        collectionIds: group.collections.map((c) => c.id),
        forecastData: mapForecastToDateValueArray(
          comparisonForecastSales,
          variantIds,
          groupMonthlyRanges,
        ),
        startDate: group.startDate,
        endDate: group.endDate,
      }
    })
  }, [comparisonGroups, comparisonForecastSales])

  return { forecastData, comparisonGroupForecastsData }
}
